import { Receipt } from 'lucide-react';
import { useState } from 'react';
import { Button, DataTable, EmptyState, type Column } from '@/components/ui';
import { usePatientBills } from '@/features/billing/api';
import { formatRM } from '@/features/billing/money';
import type { Bill } from '@/features/billing/types';
import { useBranch } from '@/features/branch/branch-context';
import { BillDrawer } from '@/pages/billing/BillDrawer';
import { BillStatusBadge } from '@/pages/billing/BillStatusBadge';
import { ReceiptDialog } from '@/pages/billing/ReceiptDialog';
import styles from './patients.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: CLINIC_TZ,
  });

/**
 * Patient record → "Billing" (UAT RL-1049, `billing:read`): this patient's
 * bills at the branch selected in the top bar (`GET /bills?patientId&status=all`),
 * with what the patient pays, paid, outstanding, status and receipt. "View"
 * opens the bill; "Receipt" the printable receipt of a paid bill.
 */
export function PatientBills({ patientId }: { patientId: string }) {
  const { selected: branch } = useBranch();
  const { data, isLoading, error, refetch } = usePatientBills(patientId);
  const [viewing, setViewing] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  if (!branch)
    return (
      <p className={styles.note}>
        Choose a branch in the branch switch to see this patient&rsquo;s bills
        there.
      </p>
    );

  const columns: Column<Bill>[] = [
    {
      key: 'no',
      header: 'Bill',
      render: (b) => <span className="mono">{b.number}</span>,
    },
    { key: 'date', header: 'Date', render: (b) => day(b.openedAt) },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (b) => (
        <>
          {formatRM(b.totalSen)}
          {b.panelSen > 0 && (
            <p className={styles.sub}>
              {b.payer?.name ?? 'Panel'} {formatRM(b.panelSen)}
            </p>
          )}
        </>
      ),
    },
    {
      key: 'paid',
      header: 'Paid',
      align: 'right',
      render: (b) => formatRM(b.paidSen),
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      align: 'right',
      render: (b) =>
        b.status === 'void' ? '—' : formatRM(Math.max(b.balanceSen, 0)),
    },
    {
      key: 'status',
      header: 'Status',
      render: (b) => <BillStatusBadge bill={b} />,
    },
    {
      key: 'receipt',
      header: 'Receipt',
      render: (b) =>
        b.receiptNumber ? <span className="mono">{b.receiptNumber}</span> : '—',
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (b) => (
        <div className={styles.actions}>
          {b.receiptNumber && (
            <Button
              size="sm"
              icon={<Receipt size={14} />}
              aria-label={`Receipt ${b.receiptNumber}`}
              onClick={() => setReceipt(b.id)}
            >
              Receipt
            </Button>
          )}
          <Button
            size="sm"
            aria-label={`View bill ${b.number}`}
            onClick={() => setViewing(b.id)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <p className={styles.sub}>Bills at {branch.name}.</p>
      <DataTable
        caption={`Bills at ${branch.name}`}
        columns={columns}
        rows={data}
        rowKey={(b) => b.id}
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        empty={
          <EmptyState
            title="No bills here"
            body={`This patient has no bills at ${branch.name}.`}
          />
        }
      />
      <BillDrawer billId={viewing} onClose={() => setViewing(null)} />
      {receipt && (
        <ReceiptDialog billId={receipt} onClose={() => setReceipt(null)} />
      )}
    </>
  );
}
